import { FormEvent, useEffect, useRef, useState } from 'react';
import { Loader2, Minimize2, SendHorizontal, X } from 'lucide-react';
import { useLanguage } from '@/app/utils/i18n';
import { AqlSeahorseLogo } from '@/app/components/branding/aql-vision-brand';
import { AqlAssistantMessage } from './AqlAssistantMessage';
import { AqlAssistantSuggestedCard } from './AqlAssistantSuggestedCard';
import { getOperationalContextChips } from './AqlOperationalContextProvider';
import { useAqlAssistant, type AqlAssistantLocalSummary } from './useAqlAssistant';

export function AqlAssistantPanel({
  open,
  onClose,
  authToken,
  summary,
  demoMode = false
}: {
  open: boolean;
  onClose: () => void;
  authToken?: string | null;
  summary?: AqlAssistantLocalSummary;
  demoMode?: boolean;
}) {
  const language = useLanguage();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    context,
    messages,
    suggestedQuestions,
    loading,
    error,
    tokenBudget,
    resetSuggestions,
    submitQuestion
  } = useAqlAssistant({ authToken, summary, demoMode });

  useEffect(() => {
    if (open) resetSuggestions();
  }, [open, resetSuggestions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, loading]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const question = draft.trim();
    if (!question || loading) return;
    setDraft('');
    submitQuestion(question);
  };

  const submitSuggested = (question: string) => {
    if (loading) return;
    setDraft('');
    submitQuestion(question);
  };

  if (!open) return null;

  const isPt = language === 'pt';
  const contextChips = getOperationalContextChips(context);
  const tokenPercent = Math.min(100, Math.max(0, (tokenBudget.used / tokenBudget.budget) * 100));
  const tokenIntl = new Intl.NumberFormat(isPt ? 'pt-PT' : 'en-US');
  const tokenDanger = tokenPercent >= 92;
  const tokenWarning = tokenPercent >= 72;
  const visibleContext = contextChips.slice(0, 3);

  return (
    <section
      data-demo-tour="marine-assistant-panel"
      className="aql-assistant-panel fixed bottom-20 right-4 top-20 z-[81] flex w-[min(440px,calc(100vw-2rem))] flex-col overflow-hidden rounded-[20px] border border-border bg-card/95 text-card-foreground shadow-[0_24px_70px_rgba(15,23,42,0.22)] backdrop-blur-xl max-sm:inset-0 max-sm:h-[100dvh] max-sm:w-full max-sm:rounded-none max-sm:border-x-0 max-sm:border-b-0 sm:right-6"
      aria-label="VISION COPILOT"
    >
      <header className="border-b border-border bg-muted/25 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-white/90 p-1.5 shadow-inner">
              <AqlSeahorseLogo className="h-full w-full object-contain" alt="" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-bold uppercase leading-tight text-foreground">VISION COPILOT</h2>
              {/* <p className="mt-1 truncate text-xs text-muted-foreground">
                {visibleContext.map((chip) => chip.value).join(' · ') || (isPt ? 'Contexto operacional' : 'Operational context')}
              </p> */}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
           
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background/70 text-muted-foreground transition-colors hover:border-primary/40 hover:bg-accent hover:text-foreground"
              aria-label="Close assistant"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
            <span className="truncate">Gemini · {tokenIntl.format(tokenBudget.used)} / {tokenIntl.format(tokenBudget.budget)}</span>
            <span className={`shrink-0 font-bold ${tokenDanger ? 'text-red-600' : tokenWarning ? 'text-amber-600' : 'text-primary'}`}>
              {tokenPercent.toFixed(tokenPercent < 10 ? 1 : 0)}%
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted shadow-inner">
            <div
              className="h-full rounded-full bg-gradient-to-r from-sky-400 via-amber-300 to-red-500 transition-[width] duration-500"
              style={{ width: `${tokenPercent}%` }}
            />
          </div>
        </div>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-3">
          {messages.length === 0 ? (
            <div className="grid gap-2">
              <div className="text-sm leading-6 text-muted-foreground">
                {isPt ? 'Pergunta pelo estado, prioridade ou próxima ação.' : 'Ask for status, priority, or next action.'}
              </div>
              {suggestedQuestions.slice(0, 3).map((question) => (
                <AqlAssistantSuggestedCard
                  key={question}
                  question={question}
                  onSelect={submitSuggested}
                  disabled={loading}
                />
              ))}
            </div>
          ) : null}

          {messages.map((message) => (
            <AqlAssistantMessage key={message.id} message={message} />
          ))}

          {loading ? (
            <div className="aql-assistant-surface flex items-center gap-2 rounded-2xl border border-border bg-muted/35 px-3 py-2.5 text-sm text-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isPt ? 'A analisar dados operacionais...' : 'Analyzing operational data...'}
            </div>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="border-t border-red-400/25 bg-red-500/10 px-4 py-2 text-xs text-red-600 dark:text-red-100">
          {error}
        </div>
      ) : null}

      <form onSubmit={submit} className="border-t border-border bg-muted/25 p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder={isPt ? 'Perguntar ao assistente...' : 'Ask the assistant...'}
            rows={2}
            disabled={tokenBudget.used >= tokenBudget.budget}
            className="min-h-11 flex-1 resize-none rounded-2xl border border-border bg-background/80 px-3 py-2.5 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground focus:border-primary/55 focus:ring-2 focus:ring-primary/12 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!draft.trim() || loading || tokenBudget.used >= tokenBudget.budget}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/28 bg-primary text-primary-foreground transition-all hover:bg-primary/85 disabled:pointer-events-none disabled:opacity-45"
            aria-label={isPt ? 'Enviar pergunta' : 'Send question'}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
          </button>
        </div>
      </form>
    </section>
  );
}
